import { useCallback, useEffect, useRef, useState } from "react";
import api, { fmtMXN, fmtMXNFull, fmtDate, todayLocal, ADMIN_EXPENSE_CATEGORIES, EXPENSE_STATUS, EXPENSE_PAYMENT_METHODS, EXPENSE_FUNDED_BY } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { StatusBadge } from "@/components/PageHeader";
import { Modal, In, Sel } from "./Projects";
import { Plus, Check, XCircle, Trash2, Paperclip, Upload, Download, FileSpreadsheet, BarChart3, Sparkles, Camera, FolderOpen, Loader2, History, Pencil } from "lucide-react";
import { toast } from "sonner";
import ChangeHistory from "@/components/ChangeHistory";
import EvidenceGallery from "@/components/EvidenceGallery";

const CATEGORY_LABEL = Object.fromEntries(ADMIN_EXPENSE_CATEGORIES.map((c) => [c.value, c.label]));
const PAYMENT_LABEL = Object.fromEntries(EXPENSE_PAYMENT_METHODS.map((m) => [m.value, m.label]));
const MESES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

const empty = {
  category: "renta_oficina",
  description: "",
  amount: 0,
  vendor: "",
  vendor_id: "",
  invoice_number: "",
  expense_date: todayLocal(),
  payment_method: "transferencia",
  notes: "",
  receipt_path: "",
  evidence_photos: [],
  is_recurring: false,
  requires_invoice: false,
  funded_by: "empresa",
  funded_by_name: "",
  cash_settlement_user_id: "",
  installments_count: "",
  first_installment_date: "",
};

export default function AdminExpensesTab() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [cashHolders, setCashHolders] = useState([]);
  useEffect(() => {
    api.get("/cash/holders").then(({ data }) => setCashHolders(data || [])).catch(() => {});
  }, []);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [filterPayment, setFilterPayment] = useState("");
  const [filterStart, setFilterStart] = useState("");
  const [filterEnd, setFilterEnd] = useState("");
  const [receiptFile, setReceiptFile] = useState(null);
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [viewerEvidence, setViewerEvidence] = useState(null);
  const [report, setReport] = useState(null);
  const [showReport, setShowReport] = useState(false);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState("");
  const [scanning, setScanning] = useState(false);
  const [scanInfo, setScanInfo] = useState(null);
  const [historyExpense, setHistoryExpense] = useState(null);
  const fileRef = useRef();
  const evidenceRef = useRef();
  const scanRef = useRef();
  const cameraRef = useRef();

  const canCreate = ["admin", "auxiliar"].includes(user?.role);
  const canApprove = ["admin", "auxiliar"].includes(user?.role);
  const canDelete = user?.role === "admin";
  const canSeeReport = ["admin", "contador"].includes(user?.role);
  const canEdit = (e) => user?.role === "admin" || (e.created_by === user?.id && e.status === "pendiente");
  const [editing, setEditing] = useState(null);

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      ...empty,
      category: e.category || "otros",
      description: e.description || "",
      amount: e.amount ?? 0,
      vendor: e.vendor || "",
      vendor_id: e.vendor_id || "",
      invoice_number: e.invoice_number || "",
      expense_date: e.expense_date || todayLocal(),
      payment_method: e.payment_method || "transferencia",
      cash_settlement_user_id: e.cash_settlement_user_id || "",
      notes: e.notes || "",
      receipt_path: e.receipt_path || "",
      evidence_photos: e.evidence_photos || [],
      is_recurring: !!e.is_recurring,
      requires_invoice: !!e.requires_invoice,
      funded_by: e.funded_by || "empresa",
      funded_by_name: e.funded_by_name || "",
    });
    setReceiptFile(null);
    setEvidenceFiles([]);
    setOpen(true);
  };

  const PAGE_SIZE = 50;
  const [page, setPage] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [sums, setSums] = useState(null);

  const load = useCallback(async () => {
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
    if (filterStatus) params.status = filterStatus;
    if (filterCategory) params.category = filterCategory;
    if (filterPayment) params.payment_method = filterPayment;
    if (filterStart) params.start_date = filterStart;
    if (filterEnd) params.end_date = filterEnd;
    const [{ data }, { data: vendorsData }, { data: usersData }] = await Promise.all([
      api.get("/admin-expenses", { params }),
      api.get("/vendors"),
      api.get("/users"),
    ]);
    setRows(data.items || []);
    setTotalCount(data.total || 0);
    setSums(data.sums || null);
    setVendors(vendorsData || []);
    setUsers((usersData || []).filter((u) => u.active !== false));
  }, [page, filterStatus, filterCategory, filterPayment, filterStart, filterEnd]);
  useEffect(() => { setPage(0); /* eslint-disable-next-line */ }, [filterStatus, filterCategory, filterPayment, filterStart, filterEnd]);
  useEffect(() => {
    const t = setTimeout(load, 150);
    return () => clearTimeout(t);
  }, [load]);

  const handleScan = async (file) => {
    if (!file) return;
    if (!navigator.onLine) {
      toast.error("Necesitas conexión para usar el escáner IA");
      return;
    }
    setScanning(true);
    setScanInfo(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin-expenses/scan-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Match vendor by name
      let matchedVendorId = "";
      if (data.vendor) {
        const v = vendors.find((vv) => vv.name && vv.name.toLowerCase().trim() === data.vendor.toLowerCase().trim());
        if (v) matchedVendorId = v.id;
      }
      setForm((f) => ({
        ...f,
        amount: data.amount ?? f.amount,
        vendor: matchedVendorId ? "" : (data.vendor || f.vendor),
        vendor_id: matchedVendorId || f.vendor_id,
        invoice_number: data.invoice_number || f.invoice_number,
        expense_date: data.expense_date || f.expense_date,
        category: data.category || f.category,
        description: data.description || f.description,
      }));
      setReceiptFile(file);
      setScanInfo({
        vendor: data.vendor,
        rfc: data.rfc,
        amount: data.amount,
        matchedVendor: !!matchedVendorId,
      });
      toast.success("Comprobante escaneado. Revisa y ajusta los datos si es necesario.");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Error al escanear";
      toast.error(typeof detail === "string" ? detail : "Error al escanear el comprobante");
    } finally {
      setScanning(false);
    }
  };

  const clearFilters = () => {
    setFilterStatus(""); setFilterCategory(""); setFilterPayment(""); setFilterStart(""); setFilterEnd("");
  };

  const save = async (ev) => {
    ev.preventDefault();
    if (form.funded_by === "persona" && !form.funded_by_name.trim()) {
      toast.error("Selecciona o escribe el nombre de la persona que puso el recurso");
      return;
    }
    const clientOpId = (window.crypto?.randomUUID && window.crypto.randomUUID()) ||
      `op-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    try {
      let receiptId = form.receipt_path;
      if (receiptFile) {
        const fd = new FormData();
        fd.append("file", receiptFile);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        receiptId = data.id;
      }
      let evidenceIds = form.evidence_photos || [];
      for (const f of evidenceFiles) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        evidenceIds = [...evidenceIds, data.id];
      }
      const payload = { ...form, amount: Math.round(Number(form.amount) * 100) / 100, client_op_id: clientOpId, receipt_path: receiptId || null, evidence_photos: evidenceIds, funded_by_name: (form.funded_by_name || "").trim() };
      payload.cash_settlement_user_id = (form.payment_method === "efectivo" && form.funded_by === "empresa") ? (form.cash_settlement_user_id || "") : "";
      if (payload.installments_count) payload.installments_count = Number(payload.installments_count);
      Object.keys(payload).forEach((k) => payload[k] === "" && delete payload[k]);
      if (editing) {
        delete payload.client_op_id;
        await api.patch(`/admin-expenses/${editing.id}`, payload);
        toast.success("Gasto administrativo actualizado");
      } else {
        await api.post("/admin-expenses", payload);
        toast.success("Gasto administrativo registrado");
      }
      setOpen(false);
      setEditing(null);
      setForm(empty);
      setReceiptFile(null);
      setEvidenceFiles([]);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar");
    }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin-expenses/${id}`, { status });
      const labels = { aprobado: "aprobado", rechazado: "rechazado", pagado: "marcado pagado" };
      toast.success(`Gasto ${labels[status] || "actualizado"}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar gasto administrativo?")) return;
    try {
      await api.delete(`/admin-expenses/${id}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const openReport = async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      const { data } = await api.get("/admin-expenses/report", { params });
      setReport(data);
      setShowReport(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo cargar el reporte");
    }
  };

  const payInstallment = async (e, it) => {
    if (!window.confirm(`¿Marcar pagada la mensualidad ${it.n}/${e.installments.length} de ${fmtMXNFull(it.amount)}?`)) return;
    try {
      await api.post(`/admin-expenses/${e.id}/installments/${it.n}/pay`, {});
      toast.success(`Mensualidad ${it.n} pagada`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al pagar mensualidad"); }
  };

  const editInstallment = async (e, it) => {
    const amt = window.prompt(`Monto de la mensualidad ${it.n}:`, it.amount);
    if (amt === null) return;
    const dd = window.prompt("Fecha de vencimiento (AAAA-MM-DD):", it.due_date);
    if (dd === null) return;
    try {
      await api.patch(`/admin-expenses/${e.id}/installments/${it.n}`, { amount: Number(amt), due_date: dd });
      toast.success("Mensualidad actualizada");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al actualizar"); }
  };

  const downloadExcel = async () => {
    try {
      const params = { year };
      if (month) params.month = month;
      const res = await api.get("/admin-expenses/report/excel", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `gastos_admin_${year}${month ? `-${String(month).padStart(2, "0")}` : ""}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("No se pudo descargar el Excel");
    }
  };

  const downloadExcelFiltered = async () => {
    try {
      const params = {};
      if (filterStatus) params.status = filterStatus;
      if (filterCategory) params.category = filterCategory;
      if (filterPayment) params.payment_method = filterPayment;
      if (filterStart) params.start_date = filterStart;
      if (filterEnd) params.end_date = filterEnd;
      const res = await api.get("/admin-expenses/export/excel", { params, responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const stamp = new Date().toISOString().slice(0, 10).replace(/-/g, "");
      link.download = `gastos_admin_filtrado_${stamp}.xlsx`;
      link.click();
      URL.revokeObjectURL(url);
      toast.success("Excel descargado");
    } catch {
      toast.error("No se pudo descargar el Excel");
    }
  };

  // ===== Render =====
  return (
    <div data-testid="admin-expenses-tab">
      {/* Filters + actions */}
      <div className="mb-4 panel p-3">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <span className="label-mono" style={{ color: "var(--magenta)" }}>// FILTROS · GASTOS ADMINISTRATIVOS</span>
          <div className="flex items-center gap-2 flex-wrap">
            {(filterStatus || filterCategory || filterPayment || filterStart || filterEnd) && (
              <button
                data-testid="admin-clear-filters-btn"
                onClick={clearFilters}
                className="text-[10px] uppercase tracking-widest font-semibold hover:underline"
                style={{ color: "var(--cyan)" }}
              >
                Limpiar filtros
              </button>
            )}
            {canSeeReport && (
              <button
                data-testid="admin-export-excel-btn"
                onClick={downloadExcelFiltered}
                title="Descargar Excel con los filtros actuales"
                className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
                style={{ borderColor: "var(--green)", color: "var(--green)" }}
              >
                <Download className="w-3.5 h-3.5" /> Excel
              </button>
            )}
            {canSeeReport && (
              <button
                data-testid="admin-report-btn"
                onClick={openReport}
                className="btn-ghost px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
                style={{ borderColor: "var(--magenta)", color: "var(--magenta)" }}
              >
                <BarChart3 className="w-3.5 h-3.5" /> Reporte
              </button>
            )}
            {canCreate && (
              <button
                data-testid="new-admin-expense-btn"
                onClick={() => { setForm(empty); setReceiptFile(null); setScanInfo(null); setOpen(true); }}
                className="btn-primary px-3 py-1.5 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-2"
              >
                <Plus className="w-3.5 h-3.5" /> Registrar gasto admin
              </button>
            )}
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <select data-testid="admin-filter-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
            className="input-cyber px-3 py-1.5 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todos los estados</option>
            {EXPENSE_STATUS.map((s) => <option key={s.value} value={s.value} style={{ background: "#0a0f1d" }}>{s.label}</option>)}
          </select>
          <select data-testid="admin-filter-category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}
            className="input-cyber px-3 py-1.5 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todas las categorías</option>
            {ADMIN_EXPENSE_CATEGORIES.map((c) => <option key={c.value} value={c.value} style={{ background: "#0a0f1d" }}>{c.label}</option>)}
          </select>
          <select data-testid="admin-filter-payment" value={filterPayment} onChange={(e) => setFilterPayment(e.target.value)}
            className="input-cyber px-3 py-1.5 text-sm">
            <option value="" style={{ background: "#0a0f1d" }}>Todos los métodos</option>
            {EXPENSE_PAYMENT_METHODS.map((m) => <option key={m.value} value={m.value} style={{ background: "#0a0f1d" }}>{m.label}</option>)}
          </select>
          <input type="date" data-testid="admin-filter-start" value={filterStart} onChange={(e) => setFilterStart(e.target.value)}
            className="input-cyber px-3 py-1.5 text-sm" />
          <input type="date" data-testid="admin-filter-end" value={filterEnd} onChange={(e) => setFilterEnd(e.target.value)}
            className="input-cyber px-3 py-1.5 text-sm" />
        </div>
      </div>

      <div className="flex items-center gap-2 flex-wrap px-1 mb-2" data-testid="admin-expenses-totals-bar">
        <span className="text-[11px] font-mono text-slate-400">{totalCount} gasto{totalCount === 1 ? "" : "s"}</span>
        {sums && (
          <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: "#22d3ee12", color: "#22d3ee", border: "1px solid #22d3ee40" }}>
            Total: <b>{fmtMXN(sums.total)}</b>
          </span>
        )}
        {sums?.pendiente > 0 && (
          <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: "#fbbf2412", color: "#fbbf24", border: "1px solid #fbbf2440" }}>
            Pendiente: <b>{fmtMXN(sums.pendiente)}</b>
          </span>
        )}
        {sums?.pagado > 0 && (
          <span className="px-2.5 py-1 rounded-sm text-[11px] font-mono" style={{ background: "#22c55e12", color: "#22c55e", border: "1px solid #22c55e40" }}>
            Pagado: <b>{fmtMXN(sums.pagado)}</b>
          </span>
        )}
      </div>

      <div className="panel overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]" data-testid="admin-expenses-table">
            <thead className="panel-header">
              <tr className="text-left">
                <Th>Fecha</Th><Th>Categoría</Th><Th>Descripción</Th>
                <Th>Proveedor</Th><Th>Método</Th><Th right>Monto</Th><Th>Estado</Th><Th right>Acciones</Th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan={8} className="text-center py-12 text-slate-500">
                  Sin gastos administrativos registrados.
                  {canCreate && " Usa el botón \"+ REGISTRAR GASTO ADMIN\" para empezar."}
                </td></tr>
              )}
              {rows.map((e) => (
                <tr key={e.id} data-testid={`admin-row-${e.id}`} className="row-hover" style={{ borderBottom: "1px solid var(--border)" }}>
                  <Td><span className="font-mono text-xs text-slate-300">{fmtDate(e.expense_date)}</span></Td>
                  <Td><span className="text-xs text-slate-300">{CATEGORY_LABEL[e.category] || e.category}</span></Td>
                  <Td>
                    <div className="font-semibold text-slate-100">{e.description}</div>
                    {e.invoice_number && <div className="text-xs text-slate-500">Folio: {e.invoice_number}</div>}
                    {e.notes && <div className="text-[11px] text-slate-500 italic mt-0.5">{e.notes}</div>}
                    {e.receipt_path && (
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}/api/files/${e.receipt_path}`}
                        target="_blank" rel="noreferrer"
                        className="text-xs hover:underline flex items-center gap-1 mt-0.5"
                        style={{ color: "var(--cyan)" }}
                      >
                        <Paperclip className="w-3 h-3" /> Comprobante
                      </a>
                    )}
                    {(e.evidence_photos || []).length > 0 && (
                      <button
                        type="button"
                        data-testid={`admin-evidence-${e.id}`}
                        onClick={() => setViewerEvidence({ photos: e.evidence_photos, title: e.description })}
                        className="text-xs hover:underline flex items-center gap-1 mt-0.5"
                        style={{ color: "#fbbf24" }}
                      >
                        <Camera className="w-3 h-3" /> Evidencias ({e.evidence_photos.length})
                      </button>
                    )}
                    {(e.installments || []).length > 0 && (
                      <div className="mt-1.5 space-y-1" data-testid={`admin-installments-${e.id}`}>
                        <div className="label-mono text-[9px]" style={{ color: "#fbbf24" }}>
                          // CRÉDITO · {e.installments.filter((i) => i.status === "pagado").length}/{e.installments.length} PAGADAS · SALDO {fmtMXN(e.installments.filter((i) => i.status !== "pagado").reduce((s, i) => s + Number(i.amount || 0), 0))}
                        </div>
                        {e.installments.map((it) => (
                          <div key={it.n} className="flex items-center gap-2 text-[11px] font-mono" data-testid={`admin-installment-${e.id}-${it.n}`}>
                            <span className="text-slate-500">#{it.n}</span>
                            <span className={it.status === "pagado" ? "text-slate-500" : (it.due_date < todayLocal() ? "text-red-400" : "text-slate-300")}>{fmtDate(it.due_date)}</span>
                            <span className={it.status === "pagado" ? "text-slate-500 line-through" : "text-slate-200"}>{fmtMXN(it.amount)}</span>
                            {it.status === "pagado" ? (
                              <span style={{ color: "var(--green)" }}>✓ {it.paid_by_name ? `${it.paid_by_name} · ` : ""}{fmtDate(it.paid_at)}</span>
                            ) : (
                              canApprove && (
                                <>
                                  <button data-testid={`pay-installment-${e.id}-${it.n}`} onClick={() => payInstallment(e, it)}
                                    className="px-1.5 py-0.5 rounded-sm text-[9px] font-bold tracking-widest" style={{ color: "var(--green)", border: "1px solid var(--green)" }}>PAGAR</button>
                                  <button data-testid={`edit-installment-${e.id}-${it.n}`} onClick={() => editInstallment(e, it)}
                                    className="text-slate-500 hover:text-cyan-300" title="Editar monto/fecha"><Pencil className="w-3 h-3" /></button>
                                </>
                              )
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </Td>
                  <Td className="text-xs text-slate-300">{e.vendor || "—"}</Td>
                  <Td>
                    <span className="label-mono text-[10px] text-slate-400">
                      {(PAYMENT_LABEL[e.payment_method] || "—").toUpperCase()}
                    </span>
                  </Td>
                  <Td right><span className="font-mono font-semibold text-slate-100">{fmtMXN(e.amount)}</span></Td>
                  <Td>
                    <StatusBadge items={EXPENSE_STATUS} value={e.status} />
                    {e.status === "aprobado" && e.approved_by_name && (
                      <div className="label-mono text-[9px] mt-1.5" style={{ color: "var(--green)" }}>
                        ✓ {e.approved_by_name} · {fmtDate(e.approved_at)}
                      </div>
                    )}
                    {e.status === "rechazado" && e.approved_by_name && (
                      <div className="label-mono text-[9px] mt-1.5" style={{ color: "var(--red)" }}>
                        ✗ {e.approved_by_name} · {fmtDate(e.approved_at)}
                      </div>
                    )}
                    {e.status === "pagado" && (
                      <div className="label-mono text-[9px] mt-1.5 space-y-0.5">
                        {e.approved_by_name && <div style={{ color: "var(--green)" }}>✓ APROBÓ {e.approved_by_name}</div>}
                        {e.paid_by_name && <div style={{ color: "var(--cyan)" }}>$ PAGÓ {e.paid_by_name} · {fmtDate(e.paid_at)}</div>}
                      </div>
                    )}
                  </Td>
                  <Td right>
                    {(e.change_history && e.change_history.length > 0) && (
                      <button
                        data-testid={`admin-history-${e.id}`}
                        onClick={() => setHistoryExpense(e)}
                        className="p-1 rounded-sm hover:bg-white/5 mr-1 relative"
                        style={{ color: "var(--magenta)" }}
                        title={`Historial de cambios (${e.change_history.length})`}
                      >
                        <History className="w-4 h-4" />
                        <span className="absolute -top-1 -right-1 text-[8px] font-bold px-1 rounded-full" style={{ background: "var(--magenta)", color: "#0a0f1d", minWidth: 12, textAlign: "center" }}>
                          {e.change_history.length}
                        </span>
                      </button>
                    )}
                    {canEdit(e) && (
                      <button data-testid={`admin-edit-${e.id}`} onClick={() => openEdit(e)} className="p-1 rounded-sm hover:bg-white/5 mr-1" style={{ color: "var(--cyan)" }} title={e.status === "pendiente" ? "Editar" : "Editar (solo admin)"}><Pencil className="w-4 h-4" /></button>
                    )}
                    {canApprove && e.status === "pendiente" && (
                      <>
                        <button data-testid={`admin-approve-${e.id}`} onClick={() => setStatus(e.id, "aprobado")} className="p-1 rounded-sm hover:bg-white/5" style={{ color: "var(--green)" }} title="Aprobar"><Check className="w-4 h-4" /></button>
                        <button data-testid={`admin-reject-${e.id}`} onClick={() => setStatus(e.id, "rechazado")} className="p-1 rounded-sm hover:bg-white/5 ml-1" style={{ color: "var(--red)" }} title="Rechazar"><XCircle className="w-4 h-4" /></button>
                      </>
                    )}
                    {canApprove && e.status === "aprobado" && (
                      <button data-testid={`admin-pay-${e.id}`} onClick={() => setStatus(e.id, "pagado")} className="text-xs font-semibold hover:underline mr-2" style={{ color: "var(--cyan)" }}>Marcar pagado</button>
                    )}
                    {canDelete && (
                      <button data-testid={`admin-delete-${e.id}`} onClick={() => remove(e.id)} className="p-1 rounded-sm hover:bg-white/5 ml-1" style={{ color: "var(--red)" }}><Trash2 className="w-4 h-4" /></button>
                    )}
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {totalCount > PAGE_SIZE && (
          <div className="flex items-center justify-between px-4 py-2.5" style={{ borderTop: "1px solid var(--border)" }} data-testid="admin-expenses-pagination">
            <button data-testid="admin-prev-page" disabled={page === 0} onClick={() => setPage(page - 1)}
              className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>
              ← Anterior
            </button>
            <span className="text-xs font-mono text-slate-400">
              Página <span className="text-slate-100 font-bold">{page + 1}</span> de {Math.max(1, Math.ceil(totalCount / PAGE_SIZE))}
              <span className="text-slate-500 ml-2">({page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalCount)} de {totalCount})</span>
            </span>
            <button data-testid="admin-next-page" disabled={(page + 1) * PAGE_SIZE >= totalCount} onClick={() => setPage(page + 1)}
              className="btn-ghost px-3 py-1.5 text-[10px] uppercase tracking-widest font-semibold rounded-sm disabled:opacity-40" style={{ border: "1px solid var(--border)" }}>
              Siguiente →
            </button>
          </div>
        )}
      </div>

      {open && (
        <Modal
          onClose={() => { setOpen(false); setEditing(null); setForm(empty); }}
          title={editing ? "Editar gasto administrativo" : "Nuevo gasto administrativo"}
          footer={
            <>
              <button type="button" onClick={() => setOpen(false)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
              <button type="submit" form="admin-expense-form" data-testid="save-admin-expense-btn" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Guardar</button>
            </>
          }
        >
          <form id="admin-expense-form" onSubmit={save} className="space-y-4">
            <div className="px-3 py-2 rounded-sm text-xs" style={{ background: "rgba(217,70,239,0.08)", border: "1px solid rgba(217,70,239,0.35)", color: "#f0abfc" }}>
              Este gasto es de la <strong>empresa</strong> (no se vincula a una obra específica). El administrador lo aprobará antes de pagarlo.
            </div>

            {/* AI Receipt Scanner */}
            <div className="panel p-3" style={{ background: "rgba(217,70,239,0.04)", border: "1px solid rgba(217,70,239,0.25)" }}>
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <Sparkles className="w-5 h-5" style={{ color: "var(--magenta)" }} />
                </div>
                <div className="flex-1">
                  <div className="label-mono mb-1" style={{ color: "var(--magenta)" }}>// ESCÁNER IA</div>
                  <div className="text-xs text-slate-300 mb-2">
                    Toma o sube una foto del comprobante administrativo. La IA llenará proveedor, monto, fecha, folio y categoría automáticamente.
                  </div>
                  <input
                    ref={scanRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp,application/pdf"
                    onChange={(e) => { const f = e.target.files[0]; if (f) handleScan(f); e.target.value = ""; }}
                    className="hidden"
                    data-testid="admin-exp-scan-input"
                  />
                  <input
                    ref={cameraRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => { const f = e.target.files[0]; if (f) handleScan(f); e.target.value = ""; }}
                    className="hidden"
                    data-testid="admin-exp-camera-input"
                  />
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      data-testid="admin-exp-camera-btn"
                      onClick={() => cameraRef.current?.click()}
                      disabled={scanning || !navigator.onLine}
                      className="btn-primary px-3 py-1.5 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                      title="Abre la cámara del dispositivo"
                    >
                      {scanning ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Analizando…</>
                      ) : (
                        <><Camera className="w-4 h-4" /> Tomar foto</>
                      )}
                    </button>
                    <button
                      type="button"
                      data-testid="admin-exp-scan-btn"
                      onClick={() => scanRef.current?.click()}
                      disabled={scanning || !navigator.onLine}
                      className="btn-ghost px-3 py-1.5 rounded-sm flex items-center gap-2 text-xs uppercase tracking-widest font-semibold disabled:opacity-50"
                    >
                      <FolderOpen className="w-4 h-4" /> Subir archivo
                    </button>
                  </div>
                  {scanInfo && (
                    <div data-testid="admin-exp-scan-result" className="mt-2 text-[11px] text-slate-300 flex flex-wrap gap-x-3 gap-y-0.5">
                      <span style={{ color: "var(--cyan)" }}>✓ Detectado:</span>
                      {scanInfo.vendor && <span><strong className="text-slate-100">{scanInfo.vendor}</strong>{scanInfo.matchedVendor && <span className="ml-1" style={{ color: "var(--green)" }}>(vinculado)</span>}</span>}
                      {scanInfo.rfc && <span>RFC: <span className="font-mono">{scanInfo.rfc}</span></span>}
                      {scanInfo.amount != null && <span>Monto: <span className="font-mono">{fmtMXN(scanInfo.amount)}</span></span>}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <Sel label="Categoría" testId="admin-exp-category" value={form.category} onChange={(v) => setForm({ ...form, category: v })}
                options={ADMIN_EXPENSE_CATEGORIES} />
              <In label="Fecha del gasto" testId="admin-exp-date" type="date" value={form.expense_date} onChange={(v) => setForm({ ...form, expense_date: v })} />
            </div>
            <In label="Descripción" testId="admin-exp-description" value={form.description} onChange={(v) => setForm({ ...form, description: v })} />
            <div className="grid grid-cols-3 gap-3">
              <In label="Monto MXN" testId="admin-exp-amount" type="number" value={form.amount} onChange={(v) => setForm({ ...form, amount: v })} />
              <Sel label="Proveedor" testId="admin-exp-vendor-select" value={form.vendor_id || ""} onChange={(v) => setForm({ ...form, vendor_id: v, vendor: v ? "" : form.vendor })}
                options={[...vendors.filter((vv) => vv.active).map((vv) => ({ value: vv.id, label: vv.name })), { value: "", label: "Otro / texto libre" }]} />
              <In label="Folio/Factura" testId="admin-exp-invoice" value={form.invoice_number} onChange={(v) => setForm({ ...form, invoice_number: v })} required={false} />
            </div>
            {!form.vendor_id && (
              <In label="Nombre del proveedor (otro)" testId="admin-exp-vendor-text" value={form.vendor} onChange={(v) => setForm({ ...form, vendor: v })} required={false} />
            )}
            <Sel label="Método de pago" testId="admin-exp-payment-method" value={form.payment_method} onChange={(v) => setForm({ ...form, payment_method: v })}
              options={EXPENSE_PAYMENT_METHODS} />
            {form.payment_method === "efectivo" && form.funded_by === "empresa" && cashHolders.length > 0 && (
              <div className="panel p-2.5" style={{ background: "rgba(34,211,238,0.05)", borderColor: "rgba(34,211,238,0.25)" }}>
                <Sel
                  label="¿Es comprobación de efectivo entregado?"
                  testId="admin-exp-cash-settlement"
                  value={form.cash_settlement_user_id || ""}
                  onChange={(v) => setForm({ ...form, cash_settlement_user_id: v })}
                  options={[
                    { value: "", label: "No — sale del saldo en caja general" },
                    ...cashHolders.map((h) => ({ value: h.id, label: `Sí — comprobación de ${h.name}` })),
                  ]}
                />
                <p className="text-[10px] text-slate-500 mt-1">
                  Si eliges a alguien, este gasto descuenta de SU saldo por comprobar (el efectivo ya salió de caja al entregárselo). Si eliges "No", descuenta directo del saldo en caja.
                </p>
              </div>
            )}

            <div className="panel p-3 space-y-3" style={{ background: "rgba(8,12,24,0.6)" }}>
              <div className="label-mono" style={{ color: "var(--cyan)" }}>// FUENTE DE RECURSOS</div>
              <div className="grid grid-cols-2 gap-3">
                <Sel
                  label="¿Quién pone el recurso?"
                  testId="admin-exp-funded-by"
                  value={form.funded_by}
                  onChange={(v) => setForm({ ...form, funded_by: v, funded_by_name: v === "empresa" ? "" : form.funded_by_name })}
                  options={EXPENSE_FUNDED_BY}
                />
                {form.funded_by === "persona" && (
                  <Sel
                    label="Nombre de la persona"
                    testId="admin-exp-funded-by-name"
                    value={users.some((u) => u.name === form.funded_by_name) ? form.funded_by_name : (form.funded_by_name ? "__other__" : "")}
                    onChange={(v) => {
                      if (v === "__other__") {
                        setForm({ ...form, funded_by_name: form.funded_by_name && !users.some((u) => u.name === form.funded_by_name) ? form.funded_by_name : " " });
                      } else {
                        setForm({ ...form, funded_by_name: v });
                      }
                    }}
                    options={[
                      { value: "", label: "— Selecciona —" },
                      ...[...users].sort((a, b) => (a.name || "").localeCompare(b.name || "")).map((u) => ({ value: u.name, label: `${u.name} (${(u.role || "").toUpperCase()})` })),
                      { value: "__other__", label: "Otro / texto libre" },
                    ]}
                  />
                )}
              </div>
              {form.funded_by === "persona" && form.funded_by_name && !users.some((u) => u.name === form.funded_by_name) && (
                <In
                  label="Nombre de la persona (otro)"
                  testId="admin-exp-funded-by-name-other"
                  value={form.funded_by_name.trim()}
                  onChange={(v) => setForm({ ...form, funded_by_name: v })}
                  required={true}
                />
              )}
              <p className="text-[11px] text-slate-500">
                Selecciona <strong>Empresa</strong> cuando el gasto sale de los recursos de la constructora.
                Selecciona <strong>Persona</strong> cuando alguien adelantó el dinero (se le deberá reembolsar).
              </p>
            </div>

            {!editing && (
              <label
                className="flex items-start gap-3 panel p-3 cursor-pointer transition-colors hover:bg-white/5"
                style={{ background: form.installments_count ? "rgba(245,158,11,0.08)" : "rgba(8,12,24,0.6)", border: `1px solid ${form.installments_count ? "rgba(245,158,11,0.4)" : "var(--border)"}` }}
              >
                <input
                  type="checkbox"
                  checked={!!form.installments_count}
                  onChange={(ev) => setForm({ ...form, installments_count: ev.target.checked ? "3" : "", first_installment_date: ev.target.checked ? (form.first_installment_date || form.expense_date) : "" })}
                  data-testid="admin-exp-credit"
                  className="mt-0.5 w-4 h-4 accent-amber-400 cursor-pointer"
                />
                <div className="flex-1">
                  <div className="label-mono text-[11px]" style={{ color: form.installments_count ? "#fbbf24" : "var(--cyan)" }}>// A CRÉDITO / MENSUALIDADES</div>
                  <p className="text-[11px] text-slate-400 mt-0.5">Compra a pagar en varias mensualidades (ej. computadora a 3 meses). El sistema genera el calendario de pagos y te avisa antes de cada vencimiento.</p>
                  {!!form.installments_count && (
                    <div className="grid grid-cols-2 gap-3 mt-2" onClick={(ev) => ev.preventDefault()}>
                      <In label="Número de mensualidades" testId="admin-exp-installments-count" type="number" value={form.installments_count}
                        onChange={(v) => setForm({ ...form, installments_count: v })} required={true} />
                      <In label="Fecha del primer pago" testId="admin-exp-first-installment" type="date" value={form.first_installment_date}
                        onChange={(v) => setForm({ ...form, first_installment_date: v })} required={true} />
                      {Number(form.installments_count) >= 2 && Number(form.amount) > 0 && (
                        <div className="col-span-2 text-[11px] font-mono" style={{ color: "#fbbf24" }} data-testid="admin-exp-installments-preview">
                          {form.installments_count} pagos de ≈ {fmtMXNFull(Math.round((Number(form.amount) / Number(form.installments_count)) * 100) / 100)} (podrás ajustar cada uno)
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </label>
            )}

            <label
              className="flex items-start gap-3 panel p-3 cursor-pointer transition-colors hover:bg-white/5"
              style={{ background: form.is_recurring ? "rgba(167,139,250,0.08)" : "rgba(8,12,24,0.6)", border: `1px solid ${form.is_recurring ? "rgba(167,139,250,0.4)" : "var(--border)"}` }}
            >
              <input
                type="checkbox"
                checked={form.is_recurring}
                onChange={(e) => setForm({ ...form, is_recurring: e.target.checked })}
                data-testid="admin-exp-recurring"
                className="mt-0.5 w-4 h-4 accent-violet-400 cursor-pointer"
              />
              <div className="flex-1">
                <div className="label-mono text-[11px]" style={{ color: form.is_recurring ? "#a78bfa" : "var(--cyan)" }}>
                  // RECURRENTE MENSUAL
                </div>
                <div className="text-xs text-slate-300 mt-0.5">
                  Marcar este gasto como <strong>recurrente</strong>. Al marcarlo como pagado, el sistema creará automáticamente el del siguiente mes en estado pendiente. Ideal para renta, internet, software, etc.
                </div>
              </div>
            </label>
            <label
              className="flex items-start gap-3 panel p-3 cursor-pointer transition-colors hover:bg-white/5"
              style={{ background: form.requires_invoice ? "rgba(217,70,239,0.06)" : "rgba(8,12,24,0.6)", border: `1px solid ${form.requires_invoice ? "rgba(217,70,239,0.4)" : "var(--border)"}` }}
            >
              <input
                type="checkbox"
                checked={!!form.requires_invoice}
                onChange={(e) => setForm({ ...form, requires_invoice: e.target.checked })}
                data-testid="admin-exp-requires-invoice"
                className="mt-0.5 w-4 h-4 accent-fuchsia-400 cursor-pointer"
              />
              <div className="flex-1">
                <div className="label-mono text-[11px]" style={{ color: form.requires_invoice ? "var(--magenta)" : "var(--cyan)" }}>
                  // REQUIERE FACTURA (CFDI DEL PROVEEDOR)
                </div>
                <div className="text-xs text-slate-300 mt-0.5">
                  Marca esta opción si esperas que el proveedor te emita un <strong>CFDI</strong> por este gasto.
                  Aparecerá en "Facturas Pendientes" hasta que captures el UUID y folio.
                </div>
              </div>
            </label>
            <div>
              <label className="label-mono block mb-1.5">Notas (opcional)</label>
              <textarea
                data-testid="admin-exp-notes"
                value={form.notes}
                rows={2}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full input-cyber px-3 py-2 text-sm"
                placeholder="Ej: Factura del mes correspondiente a marzo 2026"
              />
            </div>
            <div>
              <label className="label-mono block mb-1.5">Comprobante (imagen o PDF)</label>
              <div
                className="border border-dashed rounded-sm p-5 text-center cursor-pointer transition-colors hover:bg-white/5"
                style={{ borderColor: "var(--border-2)" }}
                onClick={() => fileRef.current?.click()}
              >
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*,application/pdf"
                  onChange={(e) => setReceiptFile(e.target.files[0])}
                  className="hidden"
                  data-testid="admin-exp-receipt-input"
                />
                <Upload className="w-5 h-5 mx-auto mb-1.5" style={{ color: "var(--cyan)" }} />
                <div className="text-xs text-slate-300">{receiptFile ? receiptFile.name : "Click para subir comprobante (max 10 MB)"}</div>
              </div>
            </div>
            <div>
              <label className="label-mono block mb-1.5">Evidencias de compra (fotos)</label>
              <div className="border border-dashed rounded-sm p-4 text-center cursor-pointer transition-colors hover:bg-white/5"
                style={{ borderColor: "rgba(251,191,36,0.35)" }}
                onClick={() => evidenceRef.current?.click()}>
                <input ref={evidenceRef} type="file" accept="image/*" multiple onChange={(ev) => { const files = Array.from(ev.target.files || []); setEvidenceFiles((prev) => [...prev, ...files]); ev.target.value = ""; }} className="hidden" data-testid="admin-exp-evidence-input" />
                <Camera className="w-5 h-5 mx-auto mb-1.5" style={{ color: "#fbbf24" }} />
                <div className="text-xs text-slate-300">
                  {evidenceFiles.length > 0 ? `${evidenceFiles.length} foto(s) por subir` : "Fotos de la compra o llegada del material"}
                </div>
              </div>
              {evidenceFiles.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mt-1.5">
                  {evidenceFiles.map((f, i) => (
                    <span key={i} className="px-2 py-0.5 rounded-sm text-[10px] font-mono flex items-center gap-1.5" style={{ background: "rgba(251,191,36,0.08)", border: "1px solid rgba(251,191,36,0.3)", color: "#fbbf24" }}>
                      {f.name.slice(0, 20)}
                      <button type="button" data-testid={`admin-remove-evidence-${i}`} onClick={() => setEvidenceFiles(evidenceFiles.filter((_, j) => j !== i))} className="font-bold">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          </form>
        </Modal>
      )}

      {viewerEvidence && (
        <EvidenceGallery photos={viewerEvidence.photos} title={viewerEvidence.title} onClose={() => setViewerEvidence(null)} />
      )}

      {showReport && report && (
        <ReportPanel
          report={report}
          year={year}
          month={month}
          setYear={setYear}
          setMonth={setMonth}
          onRefresh={openReport}
          onExcel={downloadExcel}
          onClose={() => setShowReport(false)}
        />
      )}

      {historyExpense && (
        <Modal onClose={() => setHistoryExpense(null)} title="Historial de cambios">
          <div className="mb-3 panel p-3" style={{ background: "rgba(217,70,239,0.05)" }}>
            <div className="label-mono text-[10px]" style={{ color: "var(--magenta)" }}>// GASTO ADMINISTRATIVO</div>
            <div className="text-sm font-semibold text-slate-100 mt-1">{historyExpense.description}</div>
            <div className="text-xs text-slate-500 mt-0.5">
              {fmtMXN(historyExpense.amount)} · {fmtDate(historyExpense.expense_date)}
            </div>
          </div>
          <ChangeHistory history={historyExpense.change_history || []} />
        </Modal>
      )}
    </div>
  );
}

function ReportPanel({ report, year, month, setYear, setMonth, onRefresh, onExcel, onClose }) {
  const { summary, monthly } = report;
  const totalCommitted = summary.total_committed || 1;
  const byCategorySorted = Object.entries(summary.by_category || {}).sort((a, b) => b[1] - a[1]);
  const COLORS = ["#22d3ee", "#d946ef", "#f59e0b", "#16a34a", "#7c3aed", "#dc2626", "#0891b2", "#a78bfa", "#10b981", "#fb923c", "#94a3b8"];
  const maxMonthly = Math.max(1, ...((monthly || []).map((m) => m.amount)));

  return (
    <div
      className="fixed inset-0 z-50 p-4 flex items-center justify-center"
      style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="admin-report-panel"
    >
      <div
        className="w-full max-w-4xl overflow-y-auto rounded-md shadow-2xl"
        style={{ background: "#0a0f1d", border: "1px solid var(--border-2)", maxHeight: "calc(100dvh - 2rem)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div
          className="px-6 py-4 flex items-center justify-between sticky top-0 z-10"
          style={{ background: "var(--bg-2)", borderBottom: "1px solid var(--border)" }}
        >
          <div>
            <div className="label-mono" style={{ color: "var(--magenta)" }}>// REPORTE</div>
            <h2 className="font-display font-bold text-xl text-slate-100 tracking-wide">Gastos administrativos</h2>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="admin-report-year"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="input-cyber px-2 py-1 text-xs"
            >
              {Array.from({ length: 6 }, (_, i) => new Date().getFullYear() - i).map((y) => (
                <option key={y} value={y} style={{ background: "#0a0f1d" }}>{y}</option>
              ))}
            </select>
            <select
              data-testid="admin-report-month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="input-cyber px-2 py-1 text-xs"
            >
              <option value="" style={{ background: "#0a0f1d" }}>Todo el año</option>
              {MESES.map((m, i) => (
                <option key={i} value={i + 1} style={{ background: "#0a0f1d" }}>{m}</option>
              ))}
            </select>
            <button data-testid="admin-report-refresh" onClick={onRefresh} className="btn-ghost px-3 py-1 text-xs uppercase tracking-widest font-semibold rounded-sm">
              Aplicar
            </button>
            <button data-testid="admin-report-excel" onClick={onExcel} className="btn-primary px-3 py-1 text-xs uppercase tracking-widest font-semibold rounded-sm flex items-center gap-1">
              <Download className="w-3.5 h-3.5" /> Excel
            </button>
            <button onClick={onClose} className="text-slate-500 hover:text-cyan-300 ml-1" data-testid="admin-report-close">
              <XCircle className="w-5 h-5" />
            </button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi label="Total comprometido" value={fmtMXNFull(summary.total_committed)} color="var(--magenta)" />
            <Kpi label="Pagado" value={fmtMXNFull(summary.total_paid)} color="var(--cyan)" />
            <Kpi label="Aprobado · pendiente pago" value={fmtMXNFull(summary.total_approved)} color="var(--green)" />
            <Kpi label="Pendiente aprobación" value={fmtMXNFull(summary.total_pending)} color="#f59e0b" />
          </div>

          {/* By category */}
          {byCategorySorted.length > 0 && (
            <div className="panel p-4">
              <div className="label-mono mb-3" style={{ color: "var(--cyan)" }}>// POR CATEGORÍA</div>
              <div className="space-y-2">
                {byCategorySorted.map(([cat, amt], i) => {
                  const pct = (amt / totalCommitted) * 100;
                  const color = COLORS[i % COLORS.length];
                  return (
                    <div key={cat} className="grid grid-cols-12 items-center gap-2 text-xs">
                      <div className="col-span-4 text-slate-300 truncate">{CATEGORY_LABEL[cat] || cat}</div>
                      <div className="col-span-6 h-2 rounded-sm overflow-hidden" style={{ background: "rgba(255,255,255,0.05)" }}>
                        <div className="h-full" style={{ width: `${pct}%`, background: color }} />
                      </div>
                      <div className="col-span-1 font-mono tabular-nums text-slate-200 text-right">{pct.toFixed(1)}%</div>
                      <div className="col-span-1 font-mono tabular-nums text-slate-100 text-right">{fmtMXN(amt)}</div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Monthly bars if year-only */}
          {monthly && monthly.length > 0 && (
            <div className="panel p-4">
              <div className="label-mono mb-3" style={{ color: "var(--cyan)" }}>// EVOLUCIÓN MENSUAL · {year}</div>
              <div className="grid grid-cols-12 gap-2 items-end" style={{ height: 140 }}>
                {monthly.map((m) => {
                  const h = Math.max(2, (m.amount / maxMonthly) * 100);
                  return (
                    <div key={m.month} className="flex flex-col items-center justify-end gap-1">
                      <div
                        className="w-full rounded-t-sm transition-all"
                        style={{
                          height: `${h}%`,
                          background: m.amount > 0 ? "linear-gradient(180deg, var(--magenta), var(--cyan))" : "rgba(255,255,255,0.06)",
                          minHeight: 4,
                        }}
                        title={fmtMXNFull(m.amount)}
                      />
                      <div className="text-[10px] text-slate-400 font-mono">{MESES[m.month - 1]}</div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-2 grid grid-cols-12 gap-2 text-[10px] font-mono tabular-nums text-center text-slate-400">
                {monthly.map((m) => (
                  <div key={m.month}>{m.amount > 0 ? fmtMXN(m.amount).replace("MX$", "$") : "—"}</div>
                ))}
              </div>
            </div>
          )}

          <div className="text-xs text-slate-500 flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4" />
            Descarga el Excel para enviarlo al despacho contable. Incluye Resumen, Por Categoría, Detalle y Mensual.
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, color }) {
  return (
    <div className="panel p-3">
      <div className="label-mono text-[10px] uppercase tracking-widest text-slate-400">{label}</div>
      <div className="font-display font-black text-xl tabular-nums mt-1" style={{ color }}>{value}</div>
    </div>
  );
}

const Th = ({ children, right }) => (
  <th className={`label-mono px-4 py-3 ${right ? "text-right" : ""}`}>{children}</th>
);
const Td = ({ children, right, className = "" }) => (
  <td className={`px-4 py-3 align-top ${right ? "text-right" : ""} ${className}`}>{children}</td>
);
